import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Модальное окно по макету «Новый тариф»: surface, радиус 18px, заголовок Onest 22px.
 * Для разметки «шапка / тело / подвал с линиями» используйте DialogHeader/DialogBody/DialogFooter
 * с `bordered` и `className="p-0"` у DialogContent; по умолчанию — компактный вид с отступом 24px.
 */
const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-hero/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:bg-black/60",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/** Квадратная кнопка закрытия 36px с рамкой — как в макетах диалога и карточки пользователя. */
export const closeButtonClasses =
  "grid size-9 shrink-0 place-items-center rounded-xl border border-border bg-surface text-text outline-none transition-colors hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Скрыть встроенную кнопку закрытия (если своя — в шапке). */
  hideClose?: boolean
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-card border border-border bg-surface p-6 text-text shadow-pop duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-bottom-[2%] data-[state=open]:slide-in-from-bottom-[2%]",
        className
      )}
      {...props}>
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className={cn(closeButtonClasses, "absolute right-4 top-4")}>
          <X className="h-4 w-4" />
          <span className="sr-only">Закрыть</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

export interface DialogHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Шапка с нижней линией и отступами 20/24 (для DialogContent с p-0). */
  bordered?: boolean
}

const DialogHeader = ({
  className,
  bordered,
  ...props
}: DialogHeaderProps) => (
  <div
    className={cn(
      "flex flex-col gap-1 pr-10 text-left",
      bordered && "border-b border-border-soft px-6 pb-4 pt-5 pr-16",
      className
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-4 px-6 py-5", className)} {...props} />
)
DialogBody.displayName = "DialogBody"

export interface DialogFooterProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Подвал с верхней линией и отступами 14/24 (для DialogContent с p-0). */
  bordered?: boolean
}

const DialogFooter = ({
  className,
  bordered,
  ...props
}: DialogFooterProps) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2.5 sm:flex-row sm:items-center sm:justify-end",
      bordered && "border-t border-border-soft px-6 py-3.5",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("font-display text-[22px] font-extrabold leading-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-text-2", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
